// Catálogo de productos del cotizador guiado (Fase 1 de docs/PLAN-cotizador-guiado.md).
//
// Aquí vive la lógica PURA del catálogo —sin React, sin red— para que la usen
// por igual la pantalla del catálogo, la entrevista (Fase 2) y la recomendación
// (Fase 3), y para que el servidor pueda copiar las mismas reglas sin adivinar.
//
// Cada agencia carga lo que vende, y además ve el catálogo BASE de Deskte
// (compañías y tarifas que reunió la plataforma, en el servidor:
// deskte-api/src/data/catalogoBase.ts), que puede usar, ocultar o copiar.

import { STATES_US } from './estadosUsa.js';
import { POLICY_TYPES } from './policyTypes.js';

// Los 50 estados + DC de la licencia, más Puerto Rico: hay productos que se
// venden allí (las membresías médicas, por ejemplo) aunque no se licencie igual.
export const ESTADOS = [...STATES_US, 'PR'];

export const CATEGORIAS = [
  { key: 'aca',        es: 'ACA / Mercado',             en: 'ACA / Marketplace',        color: '#38bdf8' },
  { key: 'short_term', es: 'Short-Term Medical',        en: 'Short-Term Medical',       color: '#818cf8' },
  { key: 'indemnity',  es: 'Indemnización fija',        en: 'Fixed Indemnity',          color: '#a78bfa' },
  { key: 'hospital',   es: 'Hospitalización',           en: 'Hospital',                 color: '#f472b6' },
  { key: 'accidente',  es: 'Accidente',                 en: 'Accident',                 color: '#fb923c' },
  { key: 'acceso',     es: 'Acceso médico / membresía', en: 'Medical access / membership', color: '#34d399' },
  { key: 'dental',     es: 'Dental',                    en: 'Dental',                   color: '#facc15' },
  { key: 'vision',     es: 'Visión',                    en: 'Vision',                   color: '#2dd4bf' },
  { key: 'vida',       es: 'Vida / Funeraria',          en: 'Life / Final expense',     color: '#e879f9' },
  { key: 'asistencia', es: 'Asistencia hospitalaria (FAP)', en: 'Hospital assistance (FAP)', color: '#94a3b8' },
  { key: 'otro',       es: 'Otro',                      en: 'Other',                    color: '#94a3b8' },
];
/**
 * LAS CATEGORÍAS QUE SE PUEDEN ELEGIR: las de fábrica y los TIPOS DE PÓLIZA
 * PROPIOS de la agencia, con "Otro" al final.
 *
 * Wendy Cercio (Insurance Latino), 2026-10-29: *"por favor incluye el SEGURO
 * PRIVADO en COTIZACIONES Y ASESORÍA como está en TIPO DE PÓLIZA en CLIENTES"*.
 * El 18 de octubre se hizo en el Tipo de las Cotizaciones; el catálogo del
 * Cotizador (y por tanto la Asesoría) tenía su propia lista fija. Un producto de
 * un tipo propio, al cerrar la venta, crea la póliza de ese tipo
 * (cotizadorCierre.js#ramoDePoliza).
 */
export const esTipoPropio = (key) => typeof key === 'string' && /^custom_[A-Za-z0-9_-]{1,60}$/.test(key);
const COLOR_PROPIO = '#f59e0b';
export function categoriasDelCotizador() {
  const propios = POLICY_TYPES.filter((p) => p.custom && esTipoPropio(p.id)).map((p) => ({
    key: p.id,
    es: p.labelEs || p.label || p.id,
    en: p.label || p.labelEs || p.id,
    color: COLOR_PROPIO,
    propio: true,
  }));
  const otro = CATEGORIAS[CATEGORIAS.length - 1];
  return [...CATEGORIAS.slice(0, -1), ...propios, otro];
}
export const categoriaDe = (key) => categoriasDelCotizador().find((c) => c.key === key)
  // Un tipo propio que la agencia borró: se sigue enseñando, como "Otro".
  || CATEGORIAS[CATEGORIAS.length - 1];

export const UNDERWRITING = [
  { key: 'con', es: 'Con preguntas médicas', en: 'Medically underwritten' },
  { key: 'gi',  es: 'Garantizado (sin preguntas)', en: 'Guaranteed issue' },
  { key: 'no_aplica', es: 'No aplica', en: 'Not applicable' },
];

// Cómo se cobra. "cobertura" = la tabla clásica por tipo de grupo familiar;
// "por_persona" = se suma lo de cada miembro según su edad (el dental);
// "manual" = no hay tabla, el agente escribe la prima que le dio el carrier.
export const MODOS_TARIFA = [
  { key: 'cobertura',   es: 'Por tipo de cobertura (1 persona, pareja, hijos, familia)', en: 'By coverage tier' },
  { key: 'por_persona', es: 'Por persona (se suma cada miembro)', en: 'Per person (sum each member)' },
  { key: 'manual',      es: 'Sin tabla: el agente escribe la prima', en: 'No table: agent types the premium' },
];

export const COBERTURAS = [
  { key: 'personal', es: '1 persona',         en: 'Individual' },
  { key: 'pareja',   es: 'Persona y cónyuge', en: 'Individual + spouse' },
  { key: 'hijos',    es: 'Persona e hijos',   en: 'Individual + children' },
  { key: 'familia',  es: 'Familia',           en: 'Family' },
];

export const EDAD_MAX = 120;

// El mismo tope que aplica el servidor (z.array(brochure).max(50) en
// deskte-api/src/utils/cotizadorProductos.ts): si solo estuviera allá, el PDF
// se subiría entero y el producto no se podría guardar después.
export const MAX_BROCHURES = 50;

export function productoVacio() {
  return {
    nombre: '', compania: '', categoria: 'accidente', activo: true,
    estadosModo: 'todos', estados: [],
    edadMin: '', edadMax: '',
    underwriting: 'con', preexistencias: '', esperas: '',
    beneficios: '', notasInternas: '', comision: '',
    cuotaInscripcion: '',
    tarifa: { modo: 'cobertura', porEdad: false, planes: ['Plan 1'], filas: [filaVacia(0, EDAD_MAX)] },
    requisitos: [],
    brochures: [],
    enlaceCotizador: '',
    // Advertencia para el agente (regulación, reputación, "no es un seguro"):
    // sale en amarillo en la lista y en el editor; nunca se le muestra al cliente.
    avisoAgente: '',
  };
}

export function filaVacia(desde, hasta) {
  return { desde, hasta, precios: {} };
}

// Producto del catálogo base de Deskte (deskte-api/src/data/catalogoBase.ts): lo
// comparten todas las agencias y solo lo cambia la dueña de la plataforma.
export const esBase = (p) => p?.origen === 'deskte';

// ── Dónde se vende ───────────────────────────────────────────────────────────

export function seVendeEn(p, estado) {
  if (!estado) return true;
  const lista = Array.isArray(p?.estados) ? p.estados : [];
  if (p?.estadosModo === 'solo') return lista.includes(estado);
  if (p?.estadosModo === 'excepto') return !lista.includes(estado);
  return true;
}

/**
 * Lo propio del estado gana a lo nacional de la MISMA compañía y tipo. MedBlue
 * vende en Florida su membresía de Florida ($59 / $79 / $99) y también la
 * Nacional ($40 por adulto, sin consulta en persona); la recomendación se
 * quedaba con la más barata y a un cliente de FL le salía el precio Nacional
 * (Lissette 2026-10-16: "el brochure dice claro el precio para la Florida").
 * El nacional se sigue pudiendo elegir a mano.
 */
export function primeroLoDelEstado(productos, estado) {
  const lista = productos || [];
  if (!estado) return lista;
  const clave = (p) => `${String(p?.compania || '').trim().toLowerCase()}|${p?.categoria || ''}`;
  const delEstado = new Set(lista.filter((p) => p?.estadosModo === 'solo' && seVendeEn(p, estado)).map(clave));
  return lista.filter((p) => p?.estadosModo === 'solo' || !delEstado.has(clave(p)));
}

export function textoDisponibilidad(p, es = true) {
  const lista = (Array.isArray(p?.estados) ? p.estados : []).slice().sort();
  if (p?.estadosModo === 'solo') return lista.length ? `${es ? 'Solo' : 'Only'}: ${lista.join(', ')}` : (es ? 'Ningún estado marcado' : 'No states selected');
  if (p?.estadosModo === 'excepto') return lista.length ? `${es ? 'Todos menos' : 'All except'}: ${lista.join(', ')}` : (es ? 'Todos los estados' : 'All states');
  return es ? 'Todos los estados' : 'All states';
}

export function aceptaEdad(p, edad) {
  if (edad == null || edad === '') return true;
  const n = Number(edad);
  const min = p?.edadMin === '' || p?.edadMin == null ? null : Number(p.edadMin);
  const max = p?.edadMax === '' || p?.edadMax == null ? null : Number(p.edadMax);
  if (min != null && n < min) return false;
  if (max != null && n > max) return false;
  return true;
}

// El enlace al cotizador del carrier se abre tal cual en otra pestaña: solo
// https (nada de javascript: ni http sin cifrar). Devuelve la URL limpia o null.
export function enlaceSeguro(url) {
  const s = String(url || '').trim();
  try {
    const u = new URL(s);
    return u.protocol === 'https:' ? u.href : null;
  } catch {
    return null;
  }
}

// ── Precios ──────────────────────────────────────────────────────────────────

const num = (v) => {
  if (v === '' || v == null) return null;
  const n = Number(String(v).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};

function filaParaEdad(tarifa, edad) {
  const filas = Array.isArray(tarifa?.filas) ? tarifa.filas : [];
  if (!tarifa?.porEdad) return filas[0] || null;
  const n = Number(edad);
  if (!Number.isFinite(n)) return null;
  return filas.find((f) => n >= Number(f.desde) && n <= Number(f.hasta)) || null;
}

// Precio mensual de un plan del producto.
//   cobertura: 'personal' | 'pareja' | 'hijos' | 'familia'  (modo cobertura)
//   edades:    edades de TODOS los miembros a cubrir (modo por_persona)
// Devuelve { monto, faltan } — `faltan` lista lo que impidió dar el precio,
// para que la pantalla diga POR QUÉ no hay número en vez de enseñar $0.
export function precioDe(p, plan, { edadTitular, cobertura = 'personal', edades = [] } = {}) {
  const t = p?.tarifa || {};
  if (t.modo === 'manual') return { monto: null, faltan: ['manual'] };

  if (t.modo === 'por_persona') {
    const lista = edades.length ? edades : (edadTitular != null ? [edadTitular] : []);
    if (!lista.length) return { monto: null, faltan: ['edades'] };
    let total = 0;
    for (const e of lista) {
      const fila = filaParaEdad(t, e);
      const v = num(fila?.precios?.[plan]);
      if (v == null) return { monto: null, faltan: [`edad ${e}`] };
      total += v;
    }
    return { monto: Math.round(total * 100) / 100, faltan: [], aproximado: !!t.aproximada };
  }

  const fila = filaParaEdad(t, edadTitular);
  if (!fila) return { monto: null, faltan: [t.porEdad ? 'edad' : 'tabla'] };
  let v = num(fila.precios?.[plan]?.[cobertura]);
  // "Persona + hijos" sin columna propia: hay productos que cobran por NÚMERO
  // de miembros (Assure For Life: 1 / 2 / hasta 7), así que titular + 1 hijo
  // es el precio de 2 y con más hijos el de familia (Lissette 2026-10-15:
  // "cuando son familia no lo toma").
  if (v == null && cobertura === 'hijos') v = num(fila.precios?.[plan]?.[edades.length === 2 ? 'pareja' : 'familia']);
  if (v == null) return { monto: null, faltan: [cobertura] };
  return { monto: v, faltan: [], aproximado: !!t.aproximada };
}

// ── Coberturas por plan ──────────────────────────────────────────────────────
//
// Lissette 2026-10-14: "no habla de cuánto cubre ni qué no". Cada producto
// puede traer una tabla concepto × plan (`coberturas.filas`, con el valor
// LITERAL del folleto) y la lista de lo que NO cubre (`noCubre`). Las del
// catálogo base se sacaron de los PDF de los carriers y se revisaron a mano.

/** Topes (los mismos que valida el servidor, utils/cotizadorProductos.ts). */
export const MAX_FILAS_COBERTURA = 40;
export const MAX_NO_CUBRE = 30;
/** La columna de un producto SIN planes: llave fija, la etiqueta se traduce al pintar. */
export const COLUMNA_UNICA = '__todos__';

const txt = (v) => String(v ?? '').trim();

/**
 * Lo que cubre UN plan: [{ concepto, valor }] sin vacíos. Si el producto tiene
 * una sola columna (o el plan no está en la tabla y solo hay una), se usa esa.
 */
export function coberturasDelPlan(p, plan) {
  const filas = Array.isArray(p?.coberturas?.filas) ? p.coberturas.filas : [];
  const columnas = new Set(filas.flatMap((f) => Object.keys(f?.valores || {})));
  const unica = columnas.size === 1 ? [...columnas][0] : null;
  return filas
    .map((f) => ({ concepto: txt(f?.concepto), valor: txt(f?.valores?.[plan] ?? (unica ? f?.valores?.[unica] : '')) }))
    .filter((x) => x.concepto && x.valor);
}

/** La tabla para comparar planes: { planes, filas: [{ concepto, valores: [..] }] } sin filas vacías. */
export function tablaDeCoberturas(p) {
  const filas = Array.isArray(p?.coberturas?.filas) ? p.coberturas.filas : [];
  const planes = (p?.tarifa?.planes || []).filter((x) => txt(x));
  const deTabla = [...new Set(filas.flatMap((f) => Object.keys(f?.valores || {})))];
  const cols = planes.length ? planes.filter((pl) => deTabla.includes(pl) || deTabla.length === 1) : deTabla;
  const usar = cols.length ? cols : deTabla;
  return {
    planes: usar,
    filas: filas
      .map((f) => ({ concepto: txt(f?.concepto), valores: usar.map((pl) => txt(f?.valores?.[pl] ?? (deTabla.length === 1 ? f?.valores?.[deTabla[0]] : ''))) }))
      .filter((f) => f.concepto && f.valores.some(Boolean)),
  };
}

/** Lo que NO cubre, en limpio. */
export const noCubreDe = (p) => (Array.isArray(p?.noCubre) ? p.noCubre : []).map(txt).filter(Boolean);

/** Si el producto tiene detalle de coberturas para enseñar. */
export const tieneCoberturas = (p) => tablaDeCoberturas(p).filas.length > 0 || noCubreDe(p).length > 0;

// El precio más bajo de toda la tabla, para la columna "desde $" de la lista.
export function precioDesde(p) {
  const t = p?.tarifa || {};
  if (t.modo === 'manual') return null;
  let min = null;
  for (const f of t.filas || []) {
    for (const plan of t.planes || []) {
      const celda = f.precios?.[plan];
      const valores = t.modo === 'por_persona' ? [celda] : Object.values(celda || {});
      for (const v of valores) {
        const n = num(v);
        if (n != null && (min == null || n < min)) min = n;
      }
    }
  }
  return min;
}

// Columnas de la tabla de precios en el orden en que se pintan y en que se
// pegan desde Excel: por cada plan, sus coberturas (o una sola columna por
// plan cuando se cobra por persona).
export function columnasDeTarifa(t) {
  const cols = [];
  for (const plan of t?.planes || []) {
    if (t.modo === 'por_persona') cols.push({ plan, cobertura: null });
    else for (const c of COBERTURAS) cols.push({ plan, cobertura: c.key });
  }
  return cols;
}

// "19-29", "19–29", "60+", "64 o más", "0-18", "19" → [desde, hasta]
export function leerBandaDeEdad(txt) {
  const s = String(txt || '').trim();
  let m = /^(\d{1,3})\s*[-–—a]\s*(\d{1,3})$/.exec(s);
  if (m) return [Number(m[1]), Number(m[2])];
  m = /^(\d{1,3})\s*(\+|o m[aá]s|and over|or older)$/i.exec(s);
  if (m) return [Number(m[1]), EDAD_MAX];
  m = /^(\d{1,3})$/.exec(s);
  if (m) return [Number(m[1]), Number(m[1])];
  return null;
}

// Pegar una tabla copiada de Excel o de un PDF del carrier. Cada renglón:
// [banda de edad si la tarifa va por edad] + un precio por columna, en el
// orden de columnasDeTarifa. Los renglones que no se entienden se devuelven
// aparte para avisarlos — nunca se tragan en silencio.
export function pegarTabla(t, texto) {
  const cols = columnasDeTarifa(t);
  const filas = [];
  const malos = [];
  const lineas = String(texto || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  for (const linea of lineas) {
    const celdas = linea.split(/\t|\s{2,}|;/).map((c) => c.trim()).filter((c) => c !== '');
    let banda = [0, EDAD_MAX];
    if (t.porEdad) {
      banda = leerBandaDeEdad(celdas.shift());
      if (!banda) { malos.push(linea); continue; }
    }
    const valores = celdas.map(num);
    if (!valores.length || valores.some((v) => v == null)) { malos.push(linea); continue; }
    const fila = filaVacia(banda[0], banda[1]);
    cols.forEach((col, i) => {
      if (i >= valores.length) return;
      if (col.cobertura) {
        fila.precios[col.plan] = { ...(fila.precios[col.plan] || {}), [col.cobertura]: valores[i] };
      } else {
        fila.precios[col.plan] = valores[i];
      }
    });
    filas.push(fila);
  }
  return { filas, malos };
}

// Lo que impide guardar, en palabras de agente. Lista vacía = se puede guardar.
export function problemasDelProducto(p, es = true) {
  const out = [];
  if (!String(p?.nombre || '').trim()) out.push(es ? 'Falta el nombre del producto.' : 'Product name is missing.');
  if (!String(p?.compania || '').trim()) out.push(es ? 'Falta la compañía.' : 'Carrier is missing.');
  if (String(p?.enlaceCotizador || '').trim() && !enlaceSeguro(p.enlaceCotizador)) out.push(es ? 'El enlace del cotizador tiene que empezar por https://' : 'The quoting link must start with https://');
  for (const b of p?.brochures || []) {
    if (String(b?.url || '').trim() && !enlaceSeguro(b.url)) out.push(es ? `El enlace del brochure "${b.nombre}" tiene que empezar por https://` : `Brochure link "${b.nombre}" must start with https://`);
  }
  if ((p?.brochures || []).length > MAX_BROCHURES) out.push(es ? `Un producto puede tener hasta ${MAX_BROCHURES} brochures.` : `Up to ${MAX_BROCHURES} brochures per product.`);
  if (p?.estadosModo === 'solo' && !(p.estados || []).length) out.push(es ? 'Marcó "solo en" pero no eligió ningún estado.' : '"Only in" is selected but no state was picked.');
  const t = p?.tarifa || {};
  if (t.modo !== 'manual') {
    if (!(t.planes || []).filter((x) => String(x).trim()).length) out.push(es ? 'La tarifa necesita al menos un plan.' : 'The rate table needs at least one plan.');
    if (t.porEdad) {
      const filas = (t.filas || []).slice().sort((a, b) => a.desde - b.desde);
      for (const f of filas) {
        if (!(Number(f.desde) <= Number(f.hasta))) out.push(es ? `Banda de edad al revés: ${f.desde}–${f.hasta}.` : `Age band reversed: ${f.desde}–${f.hasta}.`);
      }
      for (let i = 1; i < filas.length; i++) {
        if (Number(filas[i].desde) <= Number(filas[i - 1].hasta)) {
          out.push(es ? `Las edades ${filas[i - 1].desde}–${filas[i - 1].hasta} y ${filas[i].desde}–${filas[i].hasta} se pisan.` : `Age bands ${filas[i - 1].desde}–${filas[i - 1].hasta} and ${filas[i].desde}–${filas[i].hasta} overlap.`);
        }
      }
    }
  }
  return out;
}
